import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import * as XLSX from 'xlsx';
import { useAuth } from '../lib/auth.js';
import {
  api,
  ApiError,
  fetchCompanyOptions,
  fetchCompetentPersonOptions,
  fetchTrainerOptions,
} from '../lib/api.js';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { AsyncSearchableSelect } from '../components/AsyncSearchableSelect.js';
import { Pagination } from '../components/Pagination';
import { generateTrainingClientPDF } from '../lib/pdf-training-client.js';
import { TableLoader } from '../components/TableLoader';
import { CompanyHoverBadge } from '../components/CompanyHoverBadge';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly, formatDate } from '../lib/dateUtils';
import { useDebounce } from '../hooks/useDebounce';
import { InlinePersonnelForm } from '../components/InlinePersonnelForm.js';
import { TrainingExcelImportModal } from '../components/TrainingExcelImportModal.js';
import { generateSmartShortCodeCandidates, checkLicenceStatus, validateOptionalMultipleEmails, formatMultipleEmails } from '@gng-ehs/shared';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/BulkActionBar';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';

interface Training {
  id: string;
  certificate_number: string;
  company_id?: string;
  company_name?: string;
  company_short_code?: string;
  trainee_name: string;
  course_title: string;
  duration_days?: number;
  conducted_by_name: string;
  faculty1_personnel_id?: string;
  faculty1_name?: string;
  training_date: string;
  valid_upto: string;
  validity_text?: string;
  head_personnel_id?: string;
  head_personnel_name?: string;
  approval_text?: string;
  created_at?: string;
  updated_at?: string;
}

function formatValidityText(count: number, unit: 'YEAR' | 'MONTH'): string {
  const pad = String(count).padStart(2, '0');
  if (unit === 'YEAR') {
    return count === 1 ? `${pad} YEAR` : `${pad} YEARS`;
  } else {
    return count === 1 ? `${pad} MONTH` : `${pad} MONTHS`;
  }
}

const DEFAULT_PREDEFINED_COURSES = [
  'FIRE AND FIGHTING',
  'FIRST AID & CPR TRAINING',
  'AMMONIA HANDLING & SCBA TRAINING',
  'ELECTRICAL SAFETY',
  'CONFINED SPACE ENTRY',
  'CHEMICAL HAZARD & SPILL CONTROL',
  'WORKING AT HEIGHT & FALL PROTECTION',
];

function formatDisplayDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    if (dateStr.includes('-')) {
      const [y, m, d] = dateStr.split('T')[0].split('-');
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
    return dateStr;
  } catch {
    return dateStr || '';
  }
}

function fyOptions() {
  const cur = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => String(cur - i + 1));
}

interface Company {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name: string;
  factory_address: string;
  communication_email?: string;
}

const DEFAULT_APPROVAL_TEXT =
  'APPROVED TRAINING INSTITUTE UNDER SECTION 111A OF FACTORY ACT 1948 BY THE ' +
  'DIRECTOR OF INDUSTRIAL SAFETY & HEALTH GOVERNMENT OF GUJARAT.';

export function TrainingPage() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('training');

  const [items, setItems] = useState<Training[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [excelImportModalOpen, setExcelImportModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState<Training | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(items);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [modalError, setModalError] = useState('');

  // Global Certificate Settings state
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [settingsSavedAlert, setSettingsSavedAlert] = useState(false);
  const [headLoading, setHeadLoading] = useState(false);

  const [globalApprovalText, setGlobalApprovalText] = useState<string>(DEFAULT_APPROVAL_TEXT);
  const [globalHeadPersonnelId, setGlobalHeadPersonnelId] = useState<string>('');
  const [globalHeadPersonnelName, setGlobalHeadPersonnelName] = useState<string>('');
  const [globalHeadPersonnelObj, setGlobalHeadPersonnelObj] = useState<{
    id: string;
    full_name: string;
    designation?: string;
    signature_image_url?: string;
    competency_cert_number?: string;
    competency_valid_from?: string;
    competency_valid_upto?: string;
  } | null>(null);

  // Predefined courses list and add modal state
  const [predefinedCourses, setPredefinedCourses] = useState<string[]>(DEFAULT_PREDEFINED_COURSES);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');

  // Load persistent global settings from database API on mount
  useEffect(() => {
    setHeadLoading(true);
    api.settings.get()
      .then((res) => {
        const payload = res as unknown as { data: Record<string, string> };
        const settings = payload.data ?? {};
        if (settings.training_global_approval_text) {
          setGlobalApprovalText(settings.training_global_approval_text);
        }
        if (settings.training_predefined_courses) {
          try {
            const list = JSON.parse(settings.training_predefined_courses);
            if (Array.isArray(list) && list.length > 0) {
              setPredefinedCourses(list);
            }
          } catch { /* ignore parse error */ }
        }
        if (settings.training_global_head_personnel_id) {
          setGlobalHeadPersonnelId(settings.training_global_head_personnel_id);
        } else {
          fetchCompetentPersonOptions('')
            .then((opts) => {
              if (opts.length > 0) {
                setGlobalHeadPersonnelId(opts[0].value);
                setGlobalHeadPersonnelName(opts[0].label);
                api.settings.update({
                  training_global_head_personnel_id: opts[0].value,
                  training_global_approval_text: DEFAULT_APPROVAL_TEXT,
                }).catch(() => {});
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setHeadLoading(false));
  }, []);

  // Fetch full Head Personnel object whenever globalHeadPersonnelId changes
  useEffect(() => {
    if (globalHeadPersonnelId) {
      api.personnel.get(globalHeadPersonnelId)
        .then((r) => {
          const p = (r as { data?: {
            id: string;
            full_name: string;
            designation?: string;
            signature_image_url?: string;
            competency_cert_number?: string;
            competency_valid_from?: string;
            competency_valid_upto?: string;
          } }).data;
          if (p) {
            setGlobalHeadPersonnelObj(p);
            if (p.full_name) setGlobalHeadPersonnelName(p.full_name);
          }
        })
        .catch(() => setGlobalHeadPersonnelObj(null));
    } else {
      setGlobalHeadPersonnelObj(null);
    }
  }, [globalHeadPersonnelId]);

  const headLicenceStatus = useMemo(() => {
    return checkLicenceStatus(globalHeadPersonnelObj?.competency_valid_upto);
  }, [globalHeadPersonnelObj?.competency_valid_upto]);

  const handleSaveCourses = async (courses: string[]) => {
    setPredefinedCourses(courses);
    try {
      await api.settings.update({
        training_predefined_courses: JSON.stringify(courses),
      });
    } catch (err) {
      console.error('Failed to save predefined courses:', err);
    }
  };

  const handleAddNewCourse = async (title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const exists = predefinedCourses.some((c) => c.toLowerCase() === trimmed.toLowerCase());
    let next = predefinedCourses;
    if (!exists) {
      next = [...predefinedCourses, trimmed];
      await handleSaveCourses(next);
    }
    fields.setField('course_title', trimmed);
    setShowAddCourseModal(false);
    setNewCourseTitle('');
  };

  const handleSaveGlobalSettings = async () => {
    try {
      await api.settings.update({
        training_global_approval_text: globalApprovalText,
        training_global_head_personnel_id: globalHeadPersonnelId,
        training_predefined_courses: JSON.stringify(predefinedCourses),
      });
      if (globalHeadPersonnelId) {
        const r = await api.personnel.get(globalHeadPersonnelId);
        const p = (r as { data?: { id: string; full_name: string } }).data;
        if (p?.full_name) setGlobalHeadPersonnelName(p.full_name);
      }
      setSettingsSavedAlert(true);
      setTimeout(() => setSettingsSavedAlert(false), 3000);
    } catch {
      setError('Failed to save global settings to database');
    }
  };

  // View PDF Modal for existing certificate
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCertId, setViewCertId] = useState<string | null>(null);
  const [viewCertNumber, setViewCertNumber] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const openViewModal = async (t: Training) => {
    setViewCertId(t.id);
    setViewCertNumber(t.certificate_number);
    setViewLoading(true);
    setViewModalOpen(true);
    try {
      const url = await api.training.getPdfBlobUrl(t.id);
      setPdfBlobUrl(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch certificate PDF');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setViewCertId(null);
  };

  // Salutation and Trainee Name state
  const [salutation, setSalutation] = useState<'Mr.' | 'Mrs.' | 'Ms.' | 'Dr.'>('Mr.');
  const [singleTraineeName, setSingleTraineeName] = useState('');
  const [isBulkMode, setIsBulkMode] = useState(false);

  // Validity state
  const [validityCount, setValidityCount] = useState<number>(1);
  const [validityUnit, setValidityUnit] = useState<'YEAR' | 'MONTH'>('YEAR');
  const [validityMode, setValidityMode] = useState<'preset' | 'custom'>('preset');

  // Certificate Form Fields
  const fields = useFormFields({
    certificate_number: '',
    company_id: '',
    trainee_names: '',
    course_title: '',
    duration_days: '1',
    validity_text: '01 YEAR',
    conducted_by_name: '',
    faculty1_personnel_id: '',
    training_date: new Date().toISOString().split('T')[0],
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('training').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => setDraftCount(draftManager.listDrafts('training').length);
    update();
    const unsub = draftManager.subscribe(update);
    return unsub;
  }, []);

  const restoreTrainingDraft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditingCert(null);
    setModalError('');
    if (data.isBulkMode !== undefined) setIsBulkMode(Boolean(data.isBulkMode));
    if (data.salutation) setSalutation(data.salutation as 'Mr.' | 'Mrs.' | 'Ms.' | 'Dr.');
    if (data.singleTraineeName !== undefined) setSingleTraineeName(String(data.singleTraineeName));
    if (data.validityCount !== undefined) setValidityCount(Number(data.validityCount));
    if (data.validityUnit) setValidityUnit(data.validityUnit as 'YEAR' | 'MONTH');
    if (data.validityMode) setValidityMode(data.validityMode as 'preset' | 'custom');
    if (data.fields && typeof data.fields === 'object') {
      fields.patchValues(data.fields as any);
    } else {
      fields.patchValues(data as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restoreTrainingDraft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'training') {
        setIsResumed(true);
        restoreTrainingDraft(draft);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('resumeDraft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, restoreTrainingDraft]);

  const isTrainingDirty = useMemo(() => {
    if (!modalOpen || editingCert) return false;
    return Boolean(
      fields.values.company_id ||
      fields.values.course_title ||
      singleTraineeName ||
      fields.values.trainee_names ||
      fields.values.conducted_by_name ||
      fields.values.faculty1_personnel_id
    );
  }, [modalOpen, editingCert, fields.values, singleTraineeName]);

  const draftTitle = useMemo(() => {
    const trainee = isBulkMode ? 'Bulk Certificates' : (singleTraineeName ? `${salutation} ${singleTraineeName}` : '');
    const course = fields.values.course_title || 'Training Certificate';
    return trainee ? `${course} — ${trainee}` : course;
  }, [isBulkMode, singleTraineeName, salutation, fields.values.course_title]);

  const draftFormData = useMemo(() => ({
    fields: fields.values,
    singleTraineeName,
    salutation,
    isBulkMode,
    validityCount,
    validityUnit,
    validityMode,
  }), [fields.values, singleTraineeName, salutation, isBulkMode, validityCount, validityUnit, validityMode]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'training',
    draftId: 'dft_training_new',
    enabled: modalOpen && !editingCert,
    isDirty: isTrainingDirty,
    isResumed,
    formData: draftFormData,
    title: draftTitle,
    subtitle: fields.values.training_date ? `Date: ${formatDateOnly(fields.values.training_date)}` : undefined,
  });

  const openCreateModal = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditingCert(null);
    setModalError('');
    setIsBulkMode(false);
    setSalutation('Mr.');
    setSingleTraineeName('');
    setValidityCount(1);
    setValidityUnit('YEAR');
    setValidityMode('preset');
    const today = new Date().toISOString().split('T')[0];
    fields.reset({
      certificate_number: '',
      company_id: '',
      trainee_names: '',
      course_title: '',
      duration_days: '1',
      validity_text: '01 YEAR',
      conducted_by_name: '',
      faculty1_personnel_id: '',
      training_date: today,
    });
    setModalOpen(true);

    api.training.getNextCertNumber(today)
      .then((res) => {
        if (res.data?.next_certificate_number) {
          fields.setField('certificate_number', res.data.next_certificate_number);
        }
      })
      .catch(() => {});
  };

  const openEditModal = (t: Training) => {
    setIsResumed(false);
    setEditingCert(t);
    setModalError('');
    setIsBulkMode(false);

    // Parse salutation from t.trainee_name
    const match = (t.trainee_name || '').match(/^(Mr\.|Mrs\.|Ms\.|Dr\.)\s*(.*)$/i);
    if (match) {
      const parsedSalutation = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
      setSalutation((['Mr.', 'Mrs.', 'Ms.', 'Dr.'].includes(parsedSalutation) ? parsedSalutation : 'Mr.') as 'Mr.' | 'Mrs.' | 'Ms.' | 'Dr.');
      setSingleTraineeName(match[2] || '');
    } else {
      setSalutation('Mr.');
      setSingleTraineeName(t.trainee_name || '');
    }

    const valText = t.validity_text || '01 YEAR';
    const yearMatch = valText.match(/(\d+)\s*(?:YEAR|YR)/i);
    const monthMatch = valText.match(/(\d+)\s*(?:MONTH|MO)/i);
    if (yearMatch) {
      setValidityCount(parseInt(yearMatch[1], 10));
      setValidityUnit('YEAR');
      setValidityMode('preset');
    } else if (monthMatch) {
      setValidityCount(parseInt(monthMatch[1], 10));
      setValidityUnit('MONTH');
      setValidityMode('preset');
    } else {
      setValidityMode('custom');
    }

    fields.patchValues({
      certificate_number: t.certificate_number || '',
      company_id: t.company_id || '',
      trainee_names: t.trainee_name || '',
      course_title: t.course_title || '',
      duration_days: String(t.duration_days || 1),
      validity_text: valText,
      conducted_by_name: t.conducted_by_name || '',
      faculty1_personnel_id: t.faculty1_personnel_id || '',
      training_date: t.training_date || new Date().toISOString().split('T')[0],
    });
    setModalOpen(true);
  };

  // Selected company object for live preview
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; company_name: string; short_code: string } | null>(null);

  useEffect(() => {
    if (fields.values.company_id) {
      api.companies.get(fields.values.company_id)
        .then((res) => {
          const c = (res as { data?: { id: string; company_name: string; short_code: string } }).data;
          if (c) setSelectedCompany(c);
        })
        .catch(() => setSelectedCompany(null));
    } else {
      setSelectedCompany(null);
    }
  }, [fields.values.company_id]);

  // When faculty1_personnel_id is selected, resolve their full name by ID for conducted_by_name
  useEffect(() => {
    if (fields.values.faculty1_personnel_id) {
      api.personnel.get(fields.values.faculty1_personnel_id).then((res) => {
        const p = (res as { data?: { id: string; full_name: string } }).data;
        if (p && p.full_name) {
          fields.setField('conducted_by_name', p.full_name);
        } else {
          fields.setField('conducted_by_name', '');
        }
      }).catch(() => {
        fields.setField('conducted_by_name', '');
      });
    } else {
      fields.setField('conducted_by_name', '');
    }
  }, [fields.values.faculty1_personnel_id]);

  const [headSigDataUrl, setHeadSigDataUrl] = useState<string | null>(null);
  const [faculty1SigDataUrl, setFaculty1SigDataUrl] = useState<string | null>(null);

  // Fetch head personnel signature data URL
  useEffect(() => {
    if (!globalHeadPersonnelId) {
      setHeadSigDataUrl(null);
      return;
    }
    api.personnel.getImageDataUrl(globalHeadPersonnelId, 'signature')
      .then((dataUrl) => setHeadSigDataUrl(dataUrl))
      .catch(() => setHeadSigDataUrl(null));
  }, [globalHeadPersonnelId]);

  // Fetch faculty1 personnel signature data URL
  useEffect(() => {
    if (!fields.values.faculty1_personnel_id) {
      setFaculty1SigDataUrl(null);
      return;
    }
    api.personnel.getImageDataUrl(fields.values.faculty1_personnel_id, 'signature')
      .then((dataUrl) => setFaculty1SigDataUrl(dataUrl))
      .catch(() => setFaculty1SigDataUrl(null));
  }, [fields.values.faculty1_personnel_id]);

  // Live PDF preview blob URL for new certificate modal
  const livePdfBlobUrl = useMemo(() => {
    try {
      const traineeFormatted = isBulkMode
        ? (fields.values.trainee_names.split('\n').map((s) => s.trim()).filter(Boolean)[0] || '')
        : (singleTraineeName.trim()
            ? (/^(Mr\.|Mrs\.|Ms\.|Dr\.)\s+/i.test(singleTraineeName.trim())
                ? singleTraineeName.trim()
                : `${salutation} ${singleTraineeName.trim()}`)
            : '');

      return generateTrainingClientPDF({
        certificate_number: fields.values.certificate_number || (editingCert ? editingCert.certificate_number : `XX/Mon/${new Date().getFullYear().toString().slice(-2)}`),
        company_name: selectedCompany?.company_name || '',
        trainee_name: traineeFormatted,
        course_title: fields.values.course_title,
        conducted_by_name: fields.values.conducted_by_name,
        training_date: fields.values.training_date,
        duration_days: parseInt(fields.values.duration_days, 10) || 1,
        validity_text: fields.values.validity_text || '01 YEAR',
        head_personnel_name: globalHeadPersonnelName,
        head_personnel_designation: 'HEAD - TRAINING INSTITUTE',
        head_signature_data_url: headSigDataUrl,
        faculty1_signature_data_url: faculty1SigDataUrl,
        approval_text: globalApprovalText,
        approval_no: globalHeadPersonnelObj?.competency_cert_number || '',
        approval_date: globalHeadPersonnelObj?.competency_valid_from ? formatDisplayDate(globalHeadPersonnelObj.competency_valid_from) : '',
        approval_valid_upto: globalHeadPersonnelObj?.competency_valid_upto ? formatDisplayDate(globalHeadPersonnelObj.competency_valid_upto) : '',
      });
    } catch {
      return null;
    }
  }, [
    isBulkMode,
    fields.values.trainee_names,
    singleTraineeName,
    salutation,
    fields.values.certificate_number,
    fields.values.course_title,
    fields.values.duration_days,
    fields.values.validity_text,
    fields.values.conducted_by_name,
    fields.values.training_date,
    selectedCompany,
    globalHeadPersonnelName,
    globalHeadPersonnelObj,
    globalApprovalText,
    headSigDataUrl,
    faculty1SigDataUrl,
    editingCert,
  ]);

  const isTrainingFormValid = useMemo(() => {
    const hasTrainee = isBulkMode
      ? !!fields.values.trainee_names?.trim()
      : !!singleTraineeName.trim();
    return (
      !!fields.values.company_id &&
      hasTrainee &&
      !!fields.values.certificate_number?.trim() &&
      !!fields.values.course_title?.trim() &&
      !!fields.values.training_date?.trim() &&
      !!globalHeadPersonnelId
    );
  }, [fields.values, isBulkMode, singleTraineeName, globalHeadPersonnelId]);

  // Inline company creation
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const companyFields = useFormFields({
    short_code: '', company_name: '', occupier_name: '',
    factory_address: '', license_number: '', registration_number: '',
    nic_code: '', contact_email: '', communication_email: '',
  });

  const isQuickCompanyValid = useMemo(() => {
    return !!companyFields.values.company_name.trim() && validateOptionalMultipleEmails(companyFields.values.communication_email);
  }, [companyFields.values]);

  useEffect(() => {
    const name = companyFields.values.company_name.trim();
    if (!name) return;
    let isMounted = true;
    api.companies.suggestShortCode(name)
      .then((res) => {
        if (isMounted && res.data?.short_code) {
          companyFields.setField('short_code', res.data.short_code);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        const candidates = generateSmartShortCodeCandidates(name);
        if (candidates.length > 0) {
          companyFields.setField('short_code', candidates[0]);
        }
      });
    return () => { isMounted = false; };
  }, [companyFields.values.company_name]);

  const handleCreateCompany = async () => {
    const name = companyFields.values.company_name.trim();
    if (!name) {
      setModalError('Please enter Company Name.');
      return;
    }

    const commsEmail = companyFields.values.communication_email?.trim();
    if (!validateOptionalMultipleEmails(commsEmail)) {
      setModalError('Please enter valid Communication Email address(es), separated by commas.');
      return;
    }

    setCompanySaving(true);
    try {
      const res = await api.companies.create({
        company_name: name,
        short_code: companyFields.values.short_code.trim() || undefined,
        communication_email: formatMultipleEmails(commsEmail) || undefined,
        occupier_name: 'N/A',
        factory_address: 'N/A',
      });
      const created = (res as { data?: Company }).data;
      if (created) {
        fields.setField('company_id', created.id);
      }
      setShowCompanyForm(false);
      setModalError('');
      companyFields.reset({ short_code: '', company_name: '', occupier_name: '', factory_address: '', license_number: '', registration_number: '', nic_code: '', contact_email: '', communication_email: '' });
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setCompanySaving(false);
    }
  };

  // Inline personnel creation states
  const [showFacultyForm, setShowFacultyForm] = useState(false);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');
  const [fyFilter, setFyFilter] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'training_date' || field === 'valid_upto' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const [showHeadForm, setShowHeadForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.training.list({
      page,
      limit: 20,
      search: search || undefined,
      company_id: companyFilter || undefined,
      faculty_id: facultyFilter || undefined,
      financial_year: fyFilter || undefined,
      sortBy,
      sortOrder,
    })
      .then((r) => {
        setItems(r.data as Training[] ?? []);
        setTotalPages((r as unknown as Record<string, number>).totalPages ?? 1);
        setTotal((r as unknown as Record<string, number>).total ?? 0);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, companyFilter, facultyFilter, fyFilter, sortBy, sortOrder]);
  useDebounce(search, () => { setPage(1); load(); });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalHeadPersonnelId) {
      setModalError('Head Personnel is not configured. Please select a Head Personnel (Competent Person) in Global Certificate Settings before creating/updating certificates.');
      return;
    }
    if (headLicenceStatus.isExpired) {
      setModalError(`Selected Head Personnel (${globalHeadPersonnelObj?.full_name || 'Head Personnel'}) has an expired competency licence (expired on ${headLicenceStatus.formattedDate}). Cannot create or update training certificates.`);
      return;
    }
    const targetName = isBulkMode
      ? ''
      : (/^(Mr\.|Mrs\.|Ms\.|Dr\.)\s+/i.test(singleTraineeName.trim())
          ? singleTraineeName.trim()
          : `${salutation} ${singleTraineeName.trim()}`);

    const names = isBulkMode
      ? fields.values.trainee_names
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [targetName];

    if (!names.length || !names[0]) {
      setModalError('Please enter Trainee Name.');
      setSaving(false);
      return;
    }

    try {
      if (editingCert) {
        await api.training.update(editingCert.id, {
          certificate_number: fields.values.certificate_number?.trim() || undefined,
          company_id: fields.values.company_id,
          trainee_name: names[0] || editingCert.trainee_name,
          course_title: fields.values.course_title,
          duration_days: parseInt(fields.values.duration_days, 10) || 1,
          validity_text: fields.values.validity_text || '01 YEAR',
          conducted_by_name: fields.values.conducted_by_name,
          faculty1_personnel_id: fields.values.faculty1_personnel_id || undefined,
          training_date: fields.values.training_date,
          head_personnel_id: globalHeadPersonnelId,
          approval_text: globalApprovalText,
        });
      } else if (isBulkMode && names.length > 1) {
        await api.training.createBulk({
          company_id: fields.values.company_id,
          course_title: fields.values.course_title,
          duration_days: parseInt(fields.values.duration_days, 10) || 1,
          validity_text: fields.values.validity_text || '01 YEAR',
          conducted_by_name: fields.values.conducted_by_name,
          faculty1_personnel_id: fields.values.faculty1_personnel_id || undefined,
          training_date: fields.values.training_date,
          trainee_names: names,
          head_personnel_id: globalHeadPersonnelId,
          approval_text: globalApprovalText,
        });
      } else {
        await api.training.create({
          certificate_number: fields.values.certificate_number?.trim() || undefined,
          company_id: fields.values.company_id,
          trainee_name: names[0],
          course_title: fields.values.course_title,
          duration_days: parseInt(fields.values.duration_days, 10) || 1,
          validity_text: fields.values.validity_text || '01 YEAR',
          conducted_by_name: fields.values.conducted_by_name,
          faculty1_personnel_id: fields.values.faculty1_personnel_id || undefined,
          training_date: fields.values.training_date,
          head_personnel_id: globalHeadPersonnelId,
          approval_text: globalApprovalText,
        });
      }
      clearDraft();
      setModalOpen(false);
      setEditingCert(null);
      setModalError('');
      load();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.training.delete(deleteId); load(); }
    catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to delete'); }
    try {
      await api.training.delete(deleteId);
      setSuccess('Certificate moved to Trash.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.training.bulkDelete(Array.from(selectedIds));
      setSuccess(`Moved ${res.data?.count ?? selectedIds.size} training certificates to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected certificates');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const downloadPdf = async (id: string) => {
    try { await api.training.downloadPdf(id); }
    catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to download PDF'); }
  };

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [datePreset, setDatePreset] = useState<'3months' | '6months' | '1year' | 'all' | 'custom'>('3months');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [exportError, setExportError] = useState('');

  const getEffectiveDateRange = () => {
    const today = new Date();
    const toDateStr = today.toISOString().split('T')[0];
    if (datePreset === '3months') {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 3);
      return { fromDate: d.toISOString().split('T')[0], toDate: toDateStr };
    }
    if (datePreset === '6months') {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 6);
      return { fromDate: d.toISOString().split('T')[0], toDate: toDateStr };
    }
    if (datePreset === '1year') {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 1);
      return { fromDate: d.toISOString().split('T')[0], toDate: toDateStr };
    }
    if (datePreset === 'custom') {
      return { fromDate: customFromDate, toDate: customToDate };
    }
    return { fromDate: '', toDate: '' };
  };

  const handleExecuteExport = async () => {
    setExporting(true);
    setExportError('');
    const { fromDate, toDate } = getEffectiveDateRange();

    if (datePreset === 'custom' && (!fromDate || !toDate)) {
      setExportError('Please select both From Date and To Date for custom export.');
      setExporting(false);
      return;
    }

    try {
      const res = await api.training.list({ page: 1, limit: 10000 });
      let allItems = (res.data as Training[]) || [];

      if (fromDate) {
        allItems = allItems.filter((item) => item.training_date >= fromDate);
      }
      if (toDate) {
        allItems = allItems.filter((item) => item.training_date <= toDate);
      }

      if (allItems.length === 0) {
        setExportError('No training certificates found within the selected date frame.');
        setExporting(false);
        return;
      }

      const exportData = allItems.map((item) => ({
        'Certificate Number': item.certificate_number || '',
        'Trainee Name': item.trainee_name || '',
        'Company Name': item.company_name || '',
        'Course Title': item.course_title || '',
        'Head Personnel': item.head_personnel_name || globalHeadPersonnelName || '',
        'Faculty / Conducted By': item.faculty1_name || item.conducted_by_name || '',
        'Training Date': formatDate(item.training_date),
        'Valid Upto': formatDate(item.valid_upto),
        'Approval Text': item.approval_text || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Training Certificates');

      worksheet['!cols'] = [
        { wch: 18 }, // Cert No
        { wch: 25 }, // Trainee
        { wch: 35 }, // Company
        { wch: 25 }, // Course
        { wch: 25 }, // Head Personnel
        { wch: 25 }, // Faculty / Conducted By
        { wch: 14 }, // Date
        { wch: 14 }, // Valid Upto
        { wch: 50 }, // Approval Text
      ];

      const rangeLabel = datePreset === 'custom' ? `${fromDate}_to_${toDate}` : datePreset;
      XLSX.writeFile(workbook, `Training_Certificates_${rangeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);

      setExportModalOpen(false);
    } catch (err: unknown) {
      setExportError(err instanceof Error ? err.message : 'Failed to export excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-2 overflow-hidden">
      {/* Top Header & Settings Button */}
      <div className="shrink-0 space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Tab Pill Toggle */}
            <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
              <button
                type="button"
                onClick={() => setActiveTab('records')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === 'records'
                    ? 'bg-white text-gray-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Certificates
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('drafts')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                  activeTab === 'drafts'
                    ? 'bg-white text-amber-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Drafts</span>
                {draftCount > 0 && (
                  <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                    {draftCount}
                  </span>
                )}
              </button>
            </div>

            {activeTab === 'records' && (
              <>
                <button onClick={load} className="btn btn-secondary">Refresh</button>
                {isWrite && (
                  <button
                    onClick={() => setShowGlobalSettings((v) => !v)}
                    className="btn btn-secondary text-xs flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Global Certificate Settings
                  </button>
                )}
                <button
                  onClick={() => { setExportModalOpen(true); setExportError(''); }}
                  disabled={exporting}
                  className="btn btn-secondary text-xs flex items-center gap-1.5 text-emerald-700 hover:bg-emerald-50 border-emerald-300 cursor-pointer"
                >
                  <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Export Excel
                </button>
              </>
            )}
          </div>
          {isWrite && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExcelImportModalOpen(true)}
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 cursor-pointer shadow-sm text-xs font-semibold px-3.5 py-2"
              >
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Bulk Excel Import
              </button>
              <button onClick={openCreateModal} className="btn btn-primary">+ New Training Cert</button>
            </div>
          )}
        </div>

        {/* Persistent Global Settings Card */}
        {showGlobalSettings && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-brand-500" />
                Global Certificate Settings (Applies to all newly generated certificates)
              </h3>
              <button onClick={() => setShowGlobalSettings(false)} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
            </div>

            {settingsSavedAlert && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs font-semibold text-emerald-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Global Certificate Settings saved successfully!
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Default Head (Competent Person only)"
                hint={
                  <button
                    type="button"
                    onClick={() => setShowHeadForm((v) => !v)}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {showHeadForm ? 'Choose existing' : 'New Head Personnel'}
                  </button>
                }
              >
                <AsyncSearchableSelect
                  value={globalHeadPersonnelId}
                  onChange={(v) => setGlobalHeadPersonnelId(v)}
                  fetchOptions={fetchCompetentPersonOptions}
                  placeholder="Select Head Personnel (Competent Person only)…"
                  disabled={showHeadForm}
                />
                {globalHeadPersonnelObj && !showHeadForm && (
                  <div className="mt-2 text-xs bg-slate-50 p-2.5 rounded border border-slate-200 text-slate-600 space-y-1">
                    <p>
                      <strong className="text-slate-800">Licence Valid Upto:</strong>{' '}
                      <span className={headLicenceStatus.isExpired ? 'text-red-700 font-bold' : headLicenceStatus.isExpiringSoon ? 'text-amber-700 font-bold' : 'text-slate-700'}>
                        {headLicenceStatus.formattedDate}
                      </span>
                    </p>
                    {headLicenceStatus.isExpired && (
                      <div className="mt-1.5 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700 flex items-start gap-1.5 shadow-2xs">
                        <span>🔴</span>
                        <div>
                          <p className="font-bold text-red-800">Licence Expired — Training Certificates Blocked</p>
                          <p className="mt-0.5 text-red-700 leading-relaxed">Expired on {headLicenceStatus.formattedDate}. Please renew licence or choose an active Competent Person.</p>
                        </div>
                      </div>
                    )}
                    {headLicenceStatus.isExpiringSoon && (
                      <div className="mt-1.5 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-1.5 shadow-2xs">
                        <span>⚠️</span>
                        <div>
                          <p className="font-bold text-amber-900">Licence Expiring Soon ({headLicenceStatus.daysRemaining} days remaining)</p>
                          <p className="mt-0.5 text-amber-800 leading-relaxed">Expires on {headLicenceStatus.formattedDate}.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Full Inline Head Personnel Creation Form */}
                {showHeadForm && (
                  <div className="mt-3">
                    <InlinePersonnelForm
                      initialRole="competent_person"
                      title="Create New Head Personnel (Competent Person)"
                      onCreated={(created) => {
                        setGlobalHeadPersonnelId(created.id);
                        setGlobalHeadPersonnelName(created.full_name);
                        setShowHeadForm(false);
                      }}
                      onCancel={() => setShowHeadForm(false)}
                    />
                  </div>
                )}
                <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded p-2 text-slate-700 flex items-center justify-between">
                  <span>Active Selected Head:</span>
                  {headLoading ? (
                    <span className="inline-flex items-center text-brand-600 font-medium">
                      <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-brand-600 border-t-transparent mr-1.5" />
                      Loading personnel details…
                    </span>
                  ) : (
                    <span className="font-bold text-brand-700">{globalHeadPersonnelName || 'None Selected'}</span>
                  )}
                </div>

                {/* Section 111A Approval Details (Auto-populated from selected Head Personnel) */}
                <div className="mt-2 bg-emerald-50/70 border border-emerald-200 rounded-lg p-3 text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-emerald-900 flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-emerald-700 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Section 111A Approval Details
                    </span>
                    <span className="text-[11px] text-emerald-700">Auto-filled from selected Head Personnel</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 border-t border-emerald-200/60">
                    <div>
                      <span className="text-gray-500 block text-[11px]">Approval No.:</span>
                      <span className="font-semibold text-gray-800">{globalHeadPersonnelObj?.competency_cert_number || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-[11px]">Date:</span>
                      <span className="font-semibold text-gray-800">{formatDisplayDate(globalHeadPersonnelObj?.competency_valid_from) || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-[11px]">Valid Up to:</span>
                      <span className="font-semibold text-gray-800">{formatDisplayDate(globalHeadPersonnelObj?.competency_valid_upto) || '—'}</span>
                    </div>
                  </div>
                </div>
              </Field>

              <Field label="Default Approval Text">
                <textarea
                  className="input text-xs font-mono"
                  rows={3}
                  value={globalApprovalText}
                  onChange={(e) => setGlobalApprovalText(e.target.value)}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Approval text automatically applies to all newly generated training certificates.
                </p>
              </Field>
            </div>

            <div className="flex justify-end border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={handleSaveGlobalSettings}
                className="btn btn-primary text-xs"
              >
                Save Global Settings
              </button>
            </div>
          </div>
        )}

        {/* Search & Filters Bar */}
        {activeTab === 'records' && (
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-2 items-center w-full sm:w-auto flex-1 min-w-0">
              <div className="relative w-full sm:w-72">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search cert no, trainee, course..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input pl-9 text-sm w-full"
                />
              </div>
              <div className="w-full sm:w-64">
                <AsyncSearchableSelect
                  placeholder="Filter by Company..."
                  fetchOptions={fetchCompanyOptions}
                  value={companyFilter}
                  onChange={(val) => setCompanyFilter(val)}
                />
              </div>
              <div className="w-full sm:w-56">
                <AsyncSearchableSelect
                  placeholder="Trainer / Faculty..."
                  fetchOptions={fetchTrainerOptions}
                  value={facultyFilter}
                  onChange={(val) => setFacultyFilter(val)}
                />
              </div>
              <div className="w-full sm:w-44">
                <select
                  className="input text-sm w-full"
                  value={fyFilter}
                  onChange={(e) => setFyFilter(e.target.value)}
                >
                  <option value="">All Financial Years</option>
                  {fyOptions().map((fy) => (
                    <option key={fy} value={fy}>FY {fy}-{String(parseInt(fy) + 1).slice(-2)}</option>
                  ))}
                </select>
              </div>
            </div>
            {(search || companyFilter || facultyFilter || fyFilter) && (
              <button
                onClick={() => { setSearch(''); setCompanyFilter(''); setFacultyFilter(''); setFyFilter(''); }}
                className="text-xs text-brand-700 font-semibold hover:underline shrink-0 px-2 py-1 cursor-pointer"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
          </div>
        )}
        {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      </div>

      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="training"
            sectionTitle="Training"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <SortableHeader label="Certificate No." field="certificate_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Trainee" field="trainee_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Company" field="company_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Course" field="course_title" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Training Date" field="training_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Valid Upto" field="valid_upto" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-gray-500 py-8">No training certificates</td></tr>
                ) : items.map((t) => (
                  <tr key={t.id} className={isSelected(t.id) ? 'bg-brand-50/50 hover:bg-brand-50/70' : undefined}>
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected(t.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleSelect(t.id, e.shiftKey)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td className="font-mono text-xs font-medium text-brand-700 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openViewModal(t)}
                        className="font-mono text-xs font-bold text-brand-600 hover:text-brand-800 hover:underline cursor-pointer text-left"
                      >
                        {t.certificate_number}
                      </button>
                    </td>
                    <td className="font-medium text-sm">{t.trainee_name}</td>
                    <td className="text-sm text-gray-900 font-medium">
                      {t.company_name ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span>{t.company_name}</span>
                          {t.company_short_code && (
                            <CompanyHoverBadge shortCode={t.company_short_code} companyId={t.company_id} />
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="text-sm text-gray-500">{t.course_title}</td>
                    <td className="text-sm text-gray-500">{formatDateOnly(t.training_date)}</td>
                    <td className="text-sm text-gray-500 whitespace-nowrap">
                      <div>{formatDateOnly(t.valid_upto)}</div>
                      <span className="inline-block mt-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/80 rounded px-1.5 py-0.5">
                        {t.validity_text || '01 YEAR'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      <TimestampCell createdAt={t.created_at} updatedAt={t.updated_at} />
                    </td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <button
                          onClick={() => openViewModal(t)}
                          title="View Training Certificate PDF"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => downloadPdf(t.id)}
                          title="Download Training Certificate PDF"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>PDF</span>
                        </button>
                        {isWrite && (
                          <>
                            <button
                              onClick={() => openEditModal(t)}
                              title="Edit Certificate"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteId(t.id)}
                              title="Delete Training Certificate"
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Full-Screen Split View Modal for Creating / Editing Certificate */}
      <Modal open={modalOpen} onClose={() => { setModalOpen(false); setIsResumed(false); setModalError(''); setEditingCert(null); }} title={editingCert ? `Edit Training Certificate (${editingCert.certificate_number})` : 'New Training Certificate'} size="full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-65px)] items-stretch">
          
          {/* Left Panel: Form Inputs */}
          <div className="lg:col-span-5 space-y-4 overflow-y-auto h-full pr-3 pb-6">
            <DraftRecoveryBanner
              draft={existingDraft}
              onRestore={() => {
                const data = restoreDraft();
                if (data && existingDraft) {
                  restoreTrainingDraft({ ...existingDraft, form_data: data });
                }
              }}
              onDiscard={discardDraft}
            />

            {modalError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
                <span>{modalError}</span>
                <button type="button" onClick={() => setModalError('')} className="text-red-500 hover:text-red-700">✕</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Row 1: Certificate Number and Training Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Certificate Number"
                  required
                  hint={
                    <span className="text-[11px] text-slate-500">
                      Must be unique
                    </span>
                  }
                >
                  <div className="flex gap-2 items-center">
                    <input
                      className="input font-mono font-bold text-brand-700 uppercase flex-1"
                      value={fields.values.certificate_number}
                      onChange={(e) => fields.setField('certificate_number', e.target.value.toUpperCase())}
                      placeholder="e.g. 460/JUN/178"
                      required
                    />
                    {!editingCert && (
                      <button
                        type="button"
                        onClick={() => {
                          api.training.getNextCertNumber(fields.values.training_date).then((res) => {
                            if (res.data?.next_certificate_number) {
                              fields.setField('certificate_number', res.data.next_certificate_number);
                            }
                          });
                        }}
                        className="btn btn-secondary text-xs h-9 px-3 shrink-0 cursor-pointer"
                        title="Auto-generate next sequence certificate number"
                      >
                        Auto
                      </button>
                    )}
                  </div>
                </Field>

                <Field label="Training Date" required>
                  <input
                    className="input"
                    type="date"
                    value={fields.values.training_date}
                    onChange={(e) => {
                      fields.setField('training_date', e.target.value);
                      if (!editingCert) {
                        api.training.getNextCertNumber(e.target.value).then((res) => {
                          if (res.data?.next_certificate_number) {
                            fields.setField('certificate_number', res.data.next_certificate_number);
                          }
                        });
                      }
                    }}
                    required
                  />
                </Field>
              </div>

              {/* Row 2: Company Name */}
              <Field
                label="Company Name"
                required
                hint={
                  <button type="button" onClick={() => setShowCompanyForm((v) => !v)}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {showCompanyForm ? 'Choose existing' : 'New Company'}
                  </button>
                }
              >
                <AsyncSearchableSelect
                  value={fields.values.company_id}
                  onChange={(v) => fields.setField('company_id', v)}
                  fetchOptions={fetchCompanyOptions}
                  placeholder="Search company…"
                  disabled={showCompanyForm}
                />
              </Field>

              {/* Inline Quick Company Creation Panel (Training Only) */}
              {showCompanyForm && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-emerald-800">Quick Add Company</p>
                    <button type="button" onClick={() => setShowCompanyForm(false)} className="text-emerald-500 hover:text-emerald-700">✕</button>
                  </div>
                  <Field
                    label="Company Name"
                    required
                    hint={
                      companyFields.values.short_code ? (
                        <span className="text-[11px] font-mono font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded border border-emerald-200">
                          Auto Code: {companyFields.values.short_code}
                        </span>
                      ) : undefined
                    }
                  >
                    <input
                      className="input bg-white text-xs"
                      {...companyFields.bind('company_name')}
                      placeholder="Enter Company Name (e.g. Swastik Private Limited)"
                    />
                  </Field>
                  <Field label="Communication Email" hint="Optional, comma-separated for multiple">
                    <input
                      className="input bg-white text-xs"
                      type="text"
                      {...companyFields.bind('communication_email')}
                      placeholder="e.g. comms@company.com"
                    />
                  </Field>
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={() => setShowCompanyForm(false)} className="btn btn-secondary text-xs">Cancel</button>
                    <button type="button" onClick={handleCreateCompany} disabled={companySaving || !isQuickCompanyValid} className="btn btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed">Create & Select</button>
                  </div>
                </div>
              )}

              {/* Row 3: Trainee Name with Salutation (or Bulk Entry) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700">
                    {isBulkMode ? 'Trainee Names (Bulk Entry)' : 'Trainee Name'} <span className="text-red-500">*</span>
                  </label>
                  {!editingCert && (
                    <button
                      type="button"
                      onClick={() => setIsBulkMode(!isBulkMode)}
                      className="text-xs text-brand-600 hover:text-brand-800 font-semibold hover:underline cursor-pointer"
                    >
                      {isBulkMode ? 'Switch to Single Entry' : '+ Switch to Bulk Entry (Multiple Names)'}
                    </button>
                  )}
                </div>

                {isBulkMode ? (
                  <div>
                    <textarea
                      className="input text-sm font-medium"
                      rows={4}
                      value={fields.values.trainee_names}
                      onChange={(e) => fields.setField('trainee_names', e.target.value)}
                      placeholder="One trainee name per line, e.g.:&#10;Mr. Tapan Patel&#10;Ms. Priya Sharma&#10;Mr. Rahul Shah"
                      required
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Each trainee will receive a sequentially numbered certificate. You can include Mr./Ms./Mrs. in front of each name.
                    </p>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      className="input w-28 shrink-0 font-medium text-sm"
                      value={salutation}
                      onChange={(e) => setSalutation(e.target.value as 'Mr.' | 'Mrs.' | 'Ms.' | 'Dr.')}
                    >
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Dr.">Dr.</option>
                    </select>
                    <input
                      type="text"
                      className="input flex-1 font-medium text-sm"
                      value={singleTraineeName}
                      onChange={(e) => setSingleTraineeName(e.target.value)}
                      placeholder="Enter trainee full name (e.g. Tapan Patel)"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Row 4: Course Title and Duration */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                <div className="sm:col-span-2">
                  <Field
                    label="Course Title"
                    required
                    hint={
                      <button
                        type="button"
                        onClick={() => setShowAddCourseModal(true)}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Add New Course
                      </button>
                    }
                  >
                    <div className="relative">
                      <select
                        className="input text-sm pr-9 appearance-none cursor-pointer"
                        value={fields.values.course_title}
                        onChange={(e) => {
                          if (e.target.value === '__ADD_NEW__') {
                            setShowAddCourseModal(true);
                          } else {
                            fields.setField('course_title', e.target.value);
                          }
                        }}
                        required
                      >
                        <option value="">Select Predefined Course Title…</option>
                        {predefinedCourses.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                        {fields.values.course_title && !predefinedCourses.includes(fields.values.course_title) && (
                          <option value={fields.values.course_title}>{fields.values.course_title}</option>
                        )}
                        <option value="__ADD_NEW__">+ Add New Course Title…</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </Field>
                </div>
                <div>
                  <Field label="Duration (Days)" required>
                    <input
                      className="input text-sm font-semibold text-brand-700"
                      type="number"
                      min="1"
                      step="1"
                      value={fields.values.duration_days}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        fields.setField('duration_days', val ? String(Math.max(1, parseInt(val, 10))) : '1');
                      }}
                      placeholder="1"
                      required
                    />
                  </Field>
                </div>
              </div>

              {/* Row 5: Certificate Validity Control */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700">
                    Certificate Validity <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { label: '1 Year', count: 1, unit: 'YEAR' as const },
                      { label: '2 Years', count: 2, unit: 'YEAR' as const },
                      { label: '3 Years', count: 3, unit: 'YEAR' as const },
                      { label: '6 Months', count: 6, unit: 'MONTH' as const },
                    ].map((p) => {
                      const pText = formatValidityText(p.count, p.unit);
                      const isActive = validityMode === 'preset' && fields.values.validity_text === pText;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => {
                            setValidityMode('preset');
                            setValidityCount(p.count);
                            setValidityUnit(p.unit);
                            fields.setField('validity_text', pText);
                          }}
                          className={`px-2 py-1 rounded text-xs font-semibold transition-colors cursor-pointer ${
                            isActive
                              ? 'bg-brand-600 text-white shadow-xs'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        setValidityMode(validityMode === 'custom' ? 'preset' : 'custom');
                      }}
                      className={`px-2 py-1 rounded text-xs font-semibold transition-colors cursor-pointer ${
                        validityMode === 'custom'
                          ? 'bg-brand-600 text-white shadow-xs'
                          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      Custom
                    </button>
                  </div>
                </div>

                {validityMode === 'preset' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Duration</label>
                      <input
                        type="number"
                        min="1"
                        max="120"
                        className="input text-sm font-semibold text-brand-700"
                        value={validityCount}
                        onChange={(e) => {
                          const c = Math.max(1, parseInt(e.target.value, 10) || 1);
                          setValidityCount(c);
                          fields.setField('validity_text', formatValidityText(c, validityUnit));
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Unit</label>
                      <select
                        className="input text-sm font-semibold text-brand-700"
                        value={validityUnit}
                        onChange={(e) => {
                          const u = e.target.value as 'YEAR' | 'MONTH';
                          setValidityUnit(u);
                          fields.setField('validity_text', formatValidityText(validityCount, u));
                        }}
                      >
                        <option value="YEAR">Year(s)</option>
                        <option value="MONTH">Month(s)</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      className="input font-semibold text-sm uppercase"
                      value={fields.values.validity_text}
                      onChange={(e) => fields.setField('validity_text', e.target.value.toUpperCase())}
                      placeholder="e.g. 01 YEAR or 06 MONTHS"
                    />
                  </div>
                )}

                <div className="rounded bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] text-emerald-800 font-medium">
                  Bottom banner will display: <strong className="font-mono">NB: VALIDITY OF CERTIFICATE IS {(fields.values.validity_text || '01 YEAR').trim().toUpperCase()} FROM THE TRAINING DATE.</strong>
                </div>
              </div>

              {/* Single Faculty Personnel Selection (Trainer / Faculty only) */}
              <Field
                label="Faculty (Conducted By)"
                required
                hint={
                  <button
                    type="button"
                    onClick={() => setShowFacultyForm((v) => !v)}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {showFacultyForm ? 'Choose existing' : 'New Faculty'}
                  </button>
                }
              >
                <AsyncSearchableSelect
                  value={fields.values.faculty1_personnel_id}
                  onChange={(v) => fields.setField('faculty1_personnel_id', v)}
                  fetchOptions={fetchTrainerOptions}
                  placeholder="Select Faculty from Personnel (Trainer / Faculty)…"
                  disabled={showFacultyForm}
                />
              </Field>

              {/* Full Inline Faculty Creation Form */}
              {showFacultyForm && (
                <InlinePersonnelForm
                  initialRole="trainer"
                  title="Create New Faculty / Trainer"
                  onCreated={(created) => {
                    fields.setField('faculty1_personnel_id', created.id);
                    fields.setField('conducted_by_name', created.full_name);
                    setShowFacultyForm(false);
                  }}
                  onCancel={() => setShowFacultyForm(false)}
                />
              )}

              {/* Active Global Settings Read-Only Information Card */}
              <div className={`rounded-lg border p-3.5 space-y-2 ${!globalHeadPersonnelId ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Global Settings (Auto-Applied)</span>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      setShowGlobalSettings(true);
                    }}
                    className="text-[11px] text-brand-600 font-medium hover:underline"
                  >
                    Edit in Global Settings
                  </button>
                </div>
                <div className="text-xs space-y-1.5">
                  <div>
                    <span className="font-medium text-slate-600">Head Personnel: </span>
                    {headLoading ? (
                      <span className="inline-flex items-center text-brand-600 font-medium">
                        <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-brand-600 border-t-transparent mr-1" />
                        Resolving…
                      </span>
                    ) : globalHeadPersonnelName ? (
                      <span className="font-bold text-slate-900">{globalHeadPersonnelName}</span>
                    ) : (
                      <span className="font-bold text-amber-700">⚠️ Not Selected (Required)</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Approval Text: </span>
                    <p className="text-[11px] text-slate-600 line-clamp-2 font-mono mt-0.5">{globalApprovalText}</p>
                  </div>
                </div>
              </div>

              {!globalHeadPersonnelId && (
                <div className="rounded-lg bg-amber-50 border border-amber-300 p-3.5 flex items-center justify-between text-xs text-amber-800">
                  <span><strong>Head Personnel Required:</strong> You must select a Head Personnel in Global Settings before generating certificates.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      setShowGlobalSettings(true);
                    }}
                    className="ml-2 px-2.5 py-1 bg-amber-600 text-white font-semibold rounded hover:bg-amber-700 transition-colors shrink-0"
                  >
                    Configure Now
                  </button>
                </div>
              )}

              {modalError && <ErrorBanner message={modalError} onDismiss={() => setModalError('')} />}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setModalOpen(false); setIsResumed(false); setEditingCert(null); }} className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0">Cancel</button>
                <button type="submit" disabled={saving || !isTrainingFormValid} className="btn btn-primary text-sm h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving && <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent shrink-0" />}
                  {saving ? (editingCert ? 'Updating…' : 'Creating…') : (editingCert ? 'Update Certificate' : 'Create Certificate(s)')}
                </button>
              </div>
            </form>
          </div>

          {/* Right Panel: Live PDF Preview */}
          <div className="lg:col-span-7 h-full flex flex-col bg-slate-900 rounded-xl overflow-hidden border border-slate-800 shadow-xl">
            <div className="px-4 py-2.5 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live Certificate PDF Preview
              </span>
              <span className="text-[11px] text-slate-400">Updates automatically as you type</span>
            </div>
            <div className="flex-1 w-full bg-slate-900 p-2">
              {livePdfBlobUrl ? (
                <iframe
                  title="Live Certificate Preview"
                  src={livePdfBlobUrl}
                  className="w-full h-full rounded border-0 bg-white shadow-inner"
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm space-y-3">
                  <span className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-brand-400 border-t-transparent" />
                  <span>Generating live certificate PDF preview…</span>
                </div>
              )}
            </div>
          </div>

        </div>
      </Modal>

      {/* PDF View Modal for existing certificate */}
      <Modal open={viewModalOpen} onClose={closeViewModal} title={`Training Certificate: ${viewCertNumber}`} size="full">
        {viewLoading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-3">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-brand-600" />
            <p className="text-sm font-medium text-gray-600">Loading Training Certificate PDF Document...</p>
          </div>
        ) : pdfBlobUrl ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border border-slate-200">
              <span className="text-xs font-semibold text-gray-700">{viewCertNumber}</span>
              {viewCertId && (
                <button
                  onClick={() => downloadPdf(viewCertId)}
                  className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download PDF
                </button>
              )}
            </div>

            <iframe
              src={pdfBlobUrl}
              className="w-full h-[76vh] rounded-lg border border-slate-300 shadow-inner bg-slate-100"
              title={`Training Certificate PDF ${viewCertNumber}`}
            />
          </div>
        ) : (
          <div className="py-12 text-center text-red-600 text-sm">
            Failed to load PDF document.
          </div>
        )}
      </Modal>

      {/* Excel Export Date Frame Modal */}
      <Modal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Training Certificates to Excel"
        size="lg"
      >
        <div className="space-y-5">
          {exportError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-lg">
              {exportError}
            </div>
          )}

          <p className="text-xs font-medium text-slate-600">
            Select a date frame filter for exporting training certificate records:
          </p>

          {/* Date Frame Preset Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {[
              { id: '3months', label: '3 Months Range', desc: 'Last 90 days' },
              { id: '6months', label: '6 Months Range', desc: 'Last 180 days' },
              { id: '1year', label: '1 Year Range', desc: 'Last 365 days' },
              { id: 'all', label: 'All Time', desc: 'All certificates' },
              { id: 'custom', label: 'Custom Range', desc: 'Pick date range' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setDatePreset(item.id as typeof datePreset)}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  datePreset === item.id
                    ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 ring-2 ring-emerald-500/20 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <p className="text-xs font-bold">{item.label}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{item.desc}</p>
              </button>
            ))}
          </div>

          {/* Custom Date Pickers */}
          {datePreset === 'custom' && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">From Date (Training Date)</label>
                <input
                  type="date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  className="input text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">To Date (Training Date)</label>
                <input
                  type="date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  className="input text-xs"
                />
              </div>
            </div>
          )}

          {/* Range Summary Badge */}
          <div className="bg-slate-100 px-4 py-2.5 rounded-lg text-xs font-mono text-slate-700 flex items-center justify-between">
            <span>Filter Active: <strong>{datePreset.toUpperCase()}</strong></span>
            {datePreset !== 'all' && (
              <span>
                {getEffectiveDateRange().fromDate || 'Start'} to {getEffectiveDateRange().toDate || 'End'}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setExportModalOpen(false)}
              className="btn btn-secondary text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExecuteExport}
              disabled={exporting}
              className="btn btn-primary text-xs bg-emerald-600 hover:bg-emerald-700 border-emerald-600 flex items-center gap-1.5 cursor-pointer"
            >
              {exporting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent shrink-0" />
                  Generating Excel…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Training Certificate Excel Bulk Import Modal */}
      <TrainingExcelImportModal
        open={excelImportModalOpen}
        onClose={() => setExcelImportModalOpen(false)}
        onSuccess={() => {
          load();
        }}
        defaultHeadPersonnelId={globalHeadPersonnelId}
        defaultHeadPersonnelName={globalHeadPersonnelName}
        defaultApprovalText={globalApprovalText}
        onOpenGlobalSettings={() => setShowGlobalSettings(true)}
      />

      {/* Modal to add a new predefined course title */}
      <Modal
        open={showAddCourseModal}
        onClose={() => { setShowAddCourseModal(false); setNewCourseTitle(''); }}
        title="Add Predefined Course Title"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            Enter a course title name to add to the predefined course dropdown. It will be immediately available and saved for future certificates.
          </p>
          <Field label="Course Title Name" required hint={<span className="text-[11px] text-gray-500">e.g. CHEMICAL HAZARD & SPILL CONTROL</span>}>
            <input
              className="input text-sm"
              value={newCourseTitle}
              onChange={(e) => setNewCourseTitle(e.target.value)}
              placeholder="e.g. ELECTRICAL SAFETY AT WORKPLACE"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (newCourseTitle.trim()) {
                    handleAddNewCourse(newCourseTitle);
                  }
                }
              }}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { setShowAddCourseModal(false); setNewCourseTitle(''); }}
              className="btn btn-secondary text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleAddNewCourse(newCourseTitle)}
              disabled={!newCourseTitle.trim()}
              className="btn btn-primary text-xs disabled:opacity-50"
            >
              Save & Select Course
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        title="Move Certificate to Trash"
        message="Are you sure you want to delete this training certificate? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="certificates"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Certificates to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected training certificates? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1 min-h-[22px]">
        <label className="label mb-0">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between"><span>{message}</span><button onClick={onDismiss} className="text-red-500">✕</button></div>;
}
